import { type ReactNode, useEffect } from 'react'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'
import SiteNav from '../components/SiteNav'
import Footer from '../components/Footer'
import { openContactModal } from '../components/ContactModal'

type VisualKind = 'gpu' | 'llm'

interface CapabilityMetric {
  value: string
  label: string
}

interface CapabilityFeature {
  title: string
  body: string
  image?: string
  imageAlt?: string
}

interface CapabilityStep {
  title: string
  body: string
}

interface CapabilityUseCase {
  title: string
  body: string
}

interface CapabilityPageProps {
  path: string
  title: string
  description: string
  eyebrow: string
  heroTitle: string
  heroBody: string
  primaryCta: string
  primaryHref: string
  secondaryCta: string
  secondaryTo: string
  visual: VisualKind
  heroImage: string
  heroImageAlt: string
  metrics: CapabilityMetric[]
  featuresTitle: string
  featuresBody: string
  features: CapabilityFeature[]
  stepsTitle: string
  steps: CapabilityStep[]
  useCasesTitle: string
  useCases: CapabilityUseCase[]
  jsonLd: Record<string, unknown>
  children?: ReactNode
}

function HeroVisual({ kind, src, alt }: { kind: VisualKind; src: string; alt: string }) {
  return (
    <figure className={`od-cap-visual od-cap-visual-${kind}`}>
      <img src={src} alt={alt} width="1600" height="1000" loading="eager" decoding="async" />
    </figure>
  )
}

export default function CapabilityPage({
  path,
  title,
  description,
  eyebrow,
  heroTitle,
  heroBody,
  primaryCta,
  primaryHref,
  secondaryCta,
  secondaryTo,
  visual,
  heroImage,
  heroImageAlt,
  metrics,
  featuresTitle,
  featuresBody,
  features,
  stepsTitle,
  steps,
  useCasesTitle,
  useCases,
  jsonLd,
  children,
}: CapabilityPageProps) {
  useEffect(() => {
    document.body.classList.add('v2-capability-page')
    return () => document.body.classList.remove('v2-capability-page')
  }, [])

  const primaryOpensContact = primaryHref.startsWith('mailto:hi@shimmertech.net')

  return (
    <>
      <SEO title={title} description={description} path={path} jsonLd={jsonLd} />

      <div className={`od-home od-cap-shell od-cap-${visual}`}>
        <SiteNav />

        <main className="od-cap-main">
          <section className="od-cap-hero" aria-labelledby="capability-title">
            <div className="od-cap-hero-copy">
              <p className="od-cap-eyebrow">{eyebrow}</p>
              <h1 id="capability-title">{heroTitle}</h1>
              <p>{heroBody}</p>
              <div className="od-cap-actions" aria-label={`${eyebrow} actions`}>
                {primaryOpensContact ? (
                  <button type="button" className="od-cap-primary" onClick={openContactModal}>
                    {primaryCta}
                  </button>
                ) : (
                  <a className="od-cap-primary" href={primaryHref}>{primaryCta}</a>
                )}
                <Link className="od-cap-secondary" to={secondaryTo}>{secondaryCta}</Link>
              </div>
            </div>
            <HeroVisual kind={visual} src={heroImage} alt={heroImageAlt} />
          </section>

          <section className="od-cap-metrics" aria-label={`${eyebrow} highlights`}>
            {metrics.map((metric) => (
              <article key={metric.label}>
                <strong>{metric.value}</strong>
                <span>{metric.label}</span>
              </article>
            ))}
          </section>

          <section className="od-cap-features" aria-labelledby={`${visual}-features-title`}>
            <div className="od-cap-section-copy">
              <h2 id={`${visual}-features-title`}>{featuresTitle}</h2>
              <p>{featuresBody}</p>
            </div>
            <div className="od-cap-feature-grid">
              {features.map((feature, index) => (
                <article key={feature.title} className={`od-cap-feature od-cap-feature-${index + 1}`}>
                  {feature.image && (
                    <img
                      className="od-cap-feature-image"
                      src={feature.image}
                      alt={feature.imageAlt ?? ''}
                      width="1600"
                      height="1000"
                      loading="lazy"
                      decoding="async"
                    />
                  )}
                  <div className="od-cap-feature-copy">
                    <span>{String(index + 1).padStart(2, '0')}</span>
                    <h3>{feature.title}</h3>
                    <p>{feature.body}</p>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="od-cap-process" aria-labelledby={`${visual}-process-title`}>
            <h2 id={`${visual}-process-title`}>{stepsTitle}</h2>
            <ol>
              {steps.map((step) => (
                <li key={step.title}>
                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                </li>
              ))}
            </ol>
          </section>

          <section className="od-cap-use-cases" aria-labelledby={`${visual}-use-cases-title`}>
            <h2 id={`${visual}-use-cases-title`}>{useCasesTitle}</h2>
            <div>
              {useCases.map((item) => (
                <article key={item.title}>
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                </article>
              ))}
            </div>
          </section>

          {children}
        </main>

        <Footer />
      </div>
    </>
  )
}
